/**
 * Отбор строк списка по тому, что человек набрал в поиске.
 *
 * ⚠️ **Отбор идёт по уже загруженному списку, на клиенте.** Своей ручки API у
 * поиска нет и заводить её незачем: списки кабинета это десятки строк, они
 * целиком приезжают одним запросом, а новый маршрут с идентификатором в пути
 * означал бы ещё одну строку в `access.dbtest.ts` и ещё одно место, где можно
 * показать чужое (раздел 9 CLAUDE.md). На канале 128 кбит/с круг по сети на
 * каждую набранную букву вдобавок хуже любого отбора.
 *
 * ⚠️ **«ё» и «е» это одна буква для поиска.** Преподаватель, записавший ученика
 * Алёной, ищет её «алена» и обязан найти: свою же запись он помнит смыслом, а
 * не начертанием. Обратное направление тоже: «Алена» находится по «алёна».
 *
 * Регистр не значит ничего по той же причине. Ничего больше не нормализуется:
 * замена дефисов, пробелов и латиницы на кириллицу это догадки о том, чего
 * человек не писал, и каждая из них однажды прячет строку, которая на экране
 * есть.
 */

/** Запрос и имя приводятся к одному виду ОДНОЙ функцией: два места разъедутся. */
function fold(text: string): string {
  return text.toLowerCase().replace(/ё/g, "е");
}

/**
 * Запрос в том виде, в каком его сравнивают. Пустая строка означает «поиска
 * нет»: пробелы по краям человек набирает случайно, и список из-за них пустеть
 * не должен.
 */
export function foldQuery(query: string): string {
  return fold(query.trim());
}

/**
 * Подходит ли строка запросу.
 *
 * Вхождением подстроки, а не началом слова: преподаватель помнит фамилию,
 * прозвище в скобках или кусок названия заготовки, а не первую букву. Пустой
 * запрос подходит всему, поэтому отбор с ним возвращает список как есть.
 */
export function matchesQuery(text: string, foldedQuery: string): boolean {
  if (foldedQuery === "") return true;
  return fold(text).includes(foldedQuery);
}

/**
 * Отобрать список по запросу.
 *
 * ⚠️ Порядок НЕ меняется: ни по совпадению, ни по алфавиту. Списки кабинета
 * упорядочены сервером осмысленно (заведённый последним сверху), и
 * переставлять строки под набором значит заставлять искать глазами то, что
 * человек уже нашёл.
 *
 * ⚠️ При пустом запросе возвращается ТОТ ЖЕ массив, а не его копия: это то, что
 * видно на экране в девяноста случаях из ста, и новая ссылка на каждую
 * перерисовку означала бы перерисовку всего списка.
 */
export function filterByQuery<T>(
  items: readonly T[],
  query: string,
  textOf: (item: T) => string,
): readonly T[] {
  const folded = foldQuery(query);
  if (folded === "") return items;
  return items.filter((item) => matchesQuery(textOf(item), folded));
}
